/*
 * Created by Anup Shrestha (anup.stha012@gmail.com)
 * Copyright (c) 2022.  All rights reserved.
 * Last modified 5/10/22, 8:24 PM
 *
 */

import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { AnimatePresence, motion } from 'framer-motion';
import { useAppSelector } from 'stores';
import { Member } from 'types/MemberTypes';
import { Role } from 'types/RoleTypes';

import { useRoleDetails } from 'features/members/api/query/useRoleDetails';
import { getTabItemsForRole, TabType } from 'features/members/utils/getTabItemsForRole';

import { DeviceHistoryTab } from '../components/tabs/DeviceHistoryTab';
import Loader from 'components/Loader';
import MemberProfileHeader from 'features/members/components/others/MemberProfileHeader';
import MedicalHistoryTab from 'features/members/components/tabs/MedicalHistoryTab';
import MemberProfileOverviewTab from 'features/members/components/tabs/MemberProfileOverviewTab';
import MemberTestTab from 'features/members/components/tabs/MemberTestTab';
import SubscriptionTab from 'features/members/components/tabs/SubscriptionTab';

interface MemberProfilePage {
  member: Member;
  role: Role;
}

/**
 *
 * @constructor
 */
export function MemberProfilePage({ member, role }: MemberProfilePage) {
  const [selectedTab, setSelectedTab] = useState<TabType>(getTabItemsForRole(role.slug)[0]);
  const [tabList] = useState<TabType[]>(getTabItemsForRole(role.slug));

  return (
    <div className="flex w-full flex-col gap-8 px-10 py-8 md:px-4">
      <div className="flex w-full flex-col gap-14 rounded-2xl bg-white p-8 pb-0 shadow-sm md:gap-6">
        <MemberProfileHeader member={member} role={role} />
        <nav className="hide_scrollBar flex gap-14 overflow-x-scroll md:mt-8">
          {tabList.map((tab) => (
            <div
              key={tab}
              onClick={() => setSelectedTab(tab)}
              className={`cursor-pointer text-[1.3rem] ${
                tab === selectedTab ? 'font-medium text-primary-500' : 'font-medium text-gray-400'
              }  relative pb-6 capitalize`}
            >
              {tab}
              {tab === selectedTab ? (
                <motion.div
                  transition={{ duration: 0.1 }}
                  layoutId="underline"
                  className="absolute right-0 bottom-0 h-1 w-full bg-primary-500"
                />
              ) : null}
            </div>
          ))}
        </nav>
      </div>
      <AnimatePresence exitBeforeEnter>
        <motion.div
          key={selectedTab ?? 'empty'}
          animate={{ opacity: 1, y: 0 }}
          initial={{ opacity: 0, y: 20 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.1 }}
        >
          {selectedTab === 'overview' ? (
            <MemberProfileOverviewTab other_details={member.details} primary_details={member} />
          ) : selectedTab === 'medical history' ? (
            <MedicalHistoryTab selectedMember={member} />
          ) : selectedTab === 'tests' ? (
            <MemberTestTab selectedMember={member} />
          ) : selectedTab === 'subscriptions' ? (
            <SubscriptionTab member_id={member.id} role_id={member.role_id} />
          ) : selectedTab === 'devices' ? (
            <DeviceHistoryTab member_id={member.id} />
          ) : null}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

export const SubMemberProfilePage = () => {
  const member = useAppSelector((state) => state.members.member);
  const { data: role } = useRoleDetails(member?.role.id ?? 0);

  return (
    <>
      {' '}
      {member && role ? (
        <>
          <Helmet>
            <title>{member?.role.name} | School Health</title>
          </Helmet>
          <MemberProfilePage member={member} role={role} />{' '}
        </>
      ) : (
        <Loader />
      )}{' '}
    </>
  );
};

export const CurrentMemberProfilePage = () => {
  const member = useAppSelector((state) => state.auth.user);
  const { data: role } = useRoleDetails(member?.role.id ?? 0);

  return member && role ? (
    <>
      {' '}
      <Helmet>
        <title>Profile | School Health</title>
      </Helmet>{' '}
      <MemberProfilePage member={member} role={role} />{' '}
    </>
  ) : (
    <Loader />
  );
};
